import { BarChart3, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsGetAdDetails,
  type FacebookAdsGetAdDetailsResult,
} from 'sprites-types/workflow/tools/facebookAdsGetAdDetails'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function formatCurrency(value: string | null | undefined): string {
  if (!value) return '$0.00'
  const num = parseFloat(value)
  return `$${num.toFixed(2)}`
}

function formatNumber(value: string | null | undefined): string {
  if (!value) return '0'
  return parseFloat(value).toLocaleString()
}

function formatPercent(value: string | null | undefined): string {
  if (!value) return '0%'
  return `${parseFloat(value).toFixed(2)}%`
}

function FacebookAdsGetAdDetailsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsGetAdDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)
  const [isExpanded, setIsExpanded] = useState(true)
  const [expandedAds, setExpandedAds] = useState<Record<string, boolean>>({})
  const [expandedInsights, setExpandedInsights] = useState<Record<string, boolean>>({})

  const toggleAd = (adId: string) => {
    setExpandedAds((prev) => ({ ...prev, [adId]: !prev[adId] }))
  }

  const toggleInsightDetails = (key: string) => {
    setExpandedInsights((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const results: FacebookAdsGetAdDetailsResult[] = Array.isArray(parseResult.data)
    ? parseResult.data
    : [parseResult.data]

  const result = results[0]
  const { ads, summary } = result

  const adsWithInsights = ads.filter((ad) => ad.insights && ad.insights.length > 0).length

  if (ads.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No ad performance data found</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
          <div className="px-4 py-3">
            <div
              className={`flex items-center justify-between gap-2 ${ads.length > 1 ? 'mb-3' : ''}`}
            >
              <div className="flex items-center gap-2">
                <FaFacebook className="h-4 w-4 text-[#0064e0]" />
                <div className="text-sm font-semibold text-gray-900">Ad Details</div>
              </div>
              {summary.date_range && (
                <div className="flex items-center gap-2">
                  <div className="text-xs text-gray-400">Data may have ~24h delay</div>
                  <div className="flex items-center gap-1.5 rounded-lg border-2 border-[#0064e0] bg-blue-50 px-3 py-1.5 shadow-sm">
                    <span className="text-sm font-bold text-[#0064e0]">
                      {new Date(summary.date_range.start).toLocaleDateString()} -{' '}
                      {new Date(summary.date_range.end).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {ads.length > 1 && (
              <div
                className={`rounded-2xl border border-gray-200 bg-gray-50 p-3 ${ads.length > 1 ? 'mb-3' : ''}`}
              >
                <div className="mb-2 text-xs font-medium text-gray-500">Summary</div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-500">Ads Returned</div>
                    <div className="font-semibold text-gray-900">
                      {summary.total_ads}
                      {adsWithInsights < summary.total_ads && (
                        <span className="ml-1 text-xs font-normal text-gray-500">
                          ({adsWithInsights} with insights)
                        </span>
                      )}
                    </div>
                  </div>
                  {summary.total_spend !== null && summary.total_spend !== undefined && (
                    <div>
                      <div className="text-gray-500">Total Spend</div>
                      <div className="font-semibold text-gray-900">
                        ${summary.total_spend.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {summary.total_impressions !== null &&
                    summary.total_impressions !== undefined && (
                      <div>
                        <div className="text-gray-500">Total Impressions</div>
                        <div className="font-semibold text-gray-900">
                          {summary.total_impressions.toLocaleString()}
                        </div>
                      </div>
                    )}
                  {summary.total_clicks !== null && summary.total_clicks !== undefined && (
                    <div>
                      <div className="text-gray-500">Total Clicks</div>
                      <div className="font-semibold text-gray-900">
                        {summary.total_clicks.toLocaleString()}
                      </div>
                    </div>
                  )}
                  {summary.total_revenue !== null && summary.total_revenue !== undefined && (
                    <div>
                      <div className="text-gray-500">Total Revenue</div>
                      <div className="font-semibold text-green-600">
                        ${summary.total_revenue.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {summary.total_conversions !== null &&
                    summary.total_conversions !== undefined && (
                      <div>
                        <div className="text-gray-500">Conversions</div>
                        <div className="font-semibold text-gray-900">
                          {summary.total_conversions.toLocaleString()}
                        </div>
                      </div>
                    )}
                  {summary.roas !== null && summary.roas !== undefined && summary.roas > 0 && (
                    <div>
                      <div className="text-gray-500">ROAS (Calculated)</div>
                      <div className="font-semibold text-blue-600">{summary.roas.toFixed(2)}x</div>
                    </div>
                  )}
                  {summary.meta_roas !== null &&
                    summary.meta_roas !== undefined &&
                    summary.meta_roas > 0 && (
                      <div>
                        <div className="text-gray-500">ROAS (Meta)</div>
                        <div className="font-semibold text-blue-600">
                          {summary.meta_roas.toFixed(2)}x
                        </div>
                      </div>
                    )}
                  {summary.average_ctr !== null && summary.average_ctr !== undefined && (
                    <div>
                      <div className="text-gray-500">Avg CTR</div>
                      <div className="font-semibold text-gray-900">
                        {summary.average_ctr.toFixed(2)}%
                      </div>
                    </div>
                  )}
                  {summary.average_cpc !== null && summary.average_cpc !== undefined && (
                    <div>
                      <div className="text-gray-500">Avg CPC</div>
                      <div className="font-semibold text-gray-900">
                        ${summary.average_cpc.toFixed(2)}
                      </div>
                    </div>
                  )}
                  {summary.average_cost_per_conversion !== null &&
                    summary.average_cost_per_conversion !== undefined && (
                      <div>
                        <div className="text-gray-500">Avg Cost/Conv</div>
                        <div className="font-semibold text-orange-600">
                          ${summary.average_cost_per_conversion.toFixed(2)}
                        </div>
                      </div>
                    )}
                  {summary.average_cpm !== null && summary.average_cpm !== undefined && (
                    <div>
                      <div className="text-gray-500">Avg CPM</div>
                      <div className="font-semibold text-gray-900">
                        ${summary.average_cpm.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
                {summary.top_performing_ad && (
                  <div className="mt-3 border-t border-gray-200 pt-2">
                    <div className="text-xs text-gray-500">Top Performing</div>
                    <div className="text-xs font-medium text-gray-900">
                      {summary.top_performing_ad.name}
                    </div>
                  </div>
                )}
              </div>
            )}

            {ads.length > 1 && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex w-full items-center justify-between text-xs text-gray-600 hover:text-gray-900"
              >
                <span>{isExpanded ? 'Hide' : 'Show'} ad details</span>
                <ChevronDown
                  className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              </button>
            )}
          </div>
        </div>
      </ContextMenuWrapper>

      {isExpanded && ads.length > 0 && (
        <div className="space-y-2">
          {ads.map((ad) => {
            const isAdExpanded = expandedAds[ad.ad_id] || false
            const hasContent =
              (ad.creative && Object.keys(ad.creative).length > 0) ||
              (ad.insights && ad.insights.length > 0)

            return (
              <div
                key={ad.ad_id}
                className="mx-8 rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm backdrop-blur-[8px]"
              >
                {hasContent ? (
                  <button
                    onClick={() => toggleAd(ad.ad_id)}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
                  >
                    <ChevronDown
                      className={`mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${isAdExpanded ? 'rotate-180' : ''}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{ad.ad_name}</div>
                      <div className="text-xs text-gray-500">ID: {ad.ad_id}</div>
                      {ad.status && (
                        <span
                          className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            ad.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : ad.status === 'PAUSED'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {ad.status}
                        </span>
                      )}
                    </div>
                  </button>
                ) : (
                  <div className="flex w-full items-start gap-3 px-4 py-3">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{ad.ad_name}</div>
                      <div className="text-xs text-gray-500">ID: {ad.ad_id}</div>
                      {ad.status && (
                        <span
                          className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            ad.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : ad.status === 'PAUSED'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {ad.status}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {isAdExpanded && hasContent && (
                  <div className="px-4 pb-3">
                    {ad.creative && (
                      <div className="mb-2 rounded-xl border border-gray-200 bg-gray-50 p-2">
                        <div className="mb-2 text-xs font-medium text-gray-700">Creative</div>
                        <div className="space-y-2">
                          {ad.creative.id && (
                            <div>
                              <div className="text-xs text-gray-500">ID</div>
                              <div className="text-xs font-medium text-gray-900">
                                {ad.creative.id}
                              </div>
                            </div>
                          )}
                          {ad.creative.name && (
                            <div>
                              <div className="text-xs text-gray-500">Name</div>
                              <div className="text-xs font-medium text-gray-900">
                                {ad.creative.name}
                              </div>
                            </div>
                          )}
                          {ad.creative.title && (
                            <div>
                              <div className="text-xs text-gray-500">Title</div>
                              <div className="text-xs text-gray-900">{ad.creative.title}</div>
                            </div>
                          )}
                          {ad.creative.body && (
                            <div>
                              <div className="text-xs text-gray-500">Body</div>
                              <div className="text-xs text-gray-900">{ad.creative.body}</div>
                            </div>
                          )}
                          {ad.creative.image_hash && (
                            <div>
                              <div className="text-xs text-gray-500">Image Hash</div>
                              <div className="font-mono text-xs text-gray-900">
                                {ad.creative.image_hash}
                              </div>
                            </div>
                          )}
                          {ad.creative.video_id && (
                            <div>
                              <div className="text-xs text-gray-500">Video ID</div>
                              <div className="text-xs font-medium text-gray-900">
                                {ad.creative.video_id}
                              </div>
                            </div>
                          )}
                          {ad.creative.call_to_action_type && (
                            <div>
                              <div className="text-xs text-gray-500">Call to Action</div>
                              <div className="text-xs text-gray-900">
                                {ad.creative.call_to_action_type}
                              </div>
                            </div>
                          )}
                          {ad.creative.link_url && (
                            <div>
                              <div className="text-xs text-gray-500">Link URL</div>
                              <a
                                href={ad.creative.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {ad.creative.link_url}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {(!ad.insights || ad.insights.length === 0) && (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-center text-xs text-gray-500">
                        No insights found for this date range
                      </div>
                    )}

                    {ad.insights && ad.insights.length > 0 && (
                      <div className="space-y-2">
                        {ad.insights.map((insight, idx) => {
                          const insightKey = `${ad.ad_id}-${idx}`
                          const isInsightExpanded = expandedInsights[insightKey] || false
                          const hasAdditionalMetrics =
                            insight.reach ||
                            insight.frequency ||
                            insight.cpc ||
                            insight.cpm ||
                            insight.cpp ||
                            insight.conversions ||
                            insight.cost_per_conversion ||
                            insight.age ||
                            insight.gender ||
                            insight.country ||
                            insight.region ||
                            insight.device_platform ||
                            insight.publisher_platform ||
                            insight.platform_position

                          return (
                            <div
                              key={idx}
                              className="rounded-xl border border-gray-200 bg-gray-50 p-2"
                            >
                              {insight.date_start && insight.date_stop && (
                                <div className="mb-1 text-xs text-gray-500">
                                  {new Date(insight.date_start).toLocaleDateString()} -{' '}
                                  {new Date(insight.date_stop).toLocaleDateString()}
                                </div>
                              )}

                              {/* Basic Metrics - Always Visible */}
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                {insight.impressions && (
                                  <div className="rounded-lg bg-white p-2">
                                    <div className="text-gray-500">Impressions</div>
                                    <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                      {formatNumber(insight.impressions)}
                                    </div>
                                  </div>
                                )}
                                {insight.clicks && (
                                  <div className="rounded-lg bg-white p-2">
                                    <div className="text-gray-500">Clicks</div>
                                    <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                      {formatNumber(insight.clicks)}
                                    </div>
                                  </div>
                                )}
                                {insight.spend && (
                                  <div className="rounded-lg bg-white p-2">
                                    <div className="text-gray-500">Spend</div>
                                    <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                      {formatCurrency(insight.spend)}
                                    </div>
                                  </div>
                                )}
                                {insight.ctr && (
                                  <div className="rounded-lg bg-white p-2">
                                    <div className="text-gray-500">CTR</div>
                                    <div className="mt-0.5 text-sm font-semibold text-gray-900">
                                      {formatPercent(insight.ctr)}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Show Details Button - Only if there are additional metrics */}
                              {hasAdditionalMetrics && (
                                <button
                                  onClick={() => toggleInsightDetails(insightKey)}
                                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                                >
                                  <span>{isInsightExpanded ? 'Hide' : 'Show'} all metrics</span>
                                  <ChevronDown
                                    className={`h-3.5 w-3.5 transition-transform ${isInsightExpanded ? 'rotate-180' : ''}`}
                                  />
                                </button>
                              )}

                              {/* Additional Metrics - Conditionally Visible */}
                              {isInsightExpanded && hasAdditionalMetrics && (
                                <>
                                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                                    {insight.reach && (
                                      <div>
                                        <div className="text-gray-500">Reach</div>
                                        <div className="font-medium text-gray-900">
                                          {formatNumber(insight.reach)}
                                        </div>
                                      </div>
                                    )}
                                    {insight.frequency && (
                                      <div>
                                        <div className="text-gray-500">Frequency</div>
                                        <div className="font-medium text-gray-900">
                                          {parseFloat(insight.frequency).toFixed(2)}
                                        </div>
                                      </div>
                                    )}
                                    {insight.cpc && (
                                      <div>
                                        <div className="text-gray-500">CPC</div>
                                        <div className="font-medium text-gray-900">
                                          {formatCurrency(insight.cpc)}
                                        </div>
                                      </div>
                                    )}
                                    {insight.cpm && (
                                      <div>
                                        <div className="text-gray-500">CPM</div>
                                        <div className="font-medium text-gray-900">
                                          {formatCurrency(insight.cpm)}
                                        </div>
                                      </div>
                                    )}
                                    {insight.cpp && (
                                      <div>
                                        <div className="text-gray-500">CPP</div>
                                        <div className="font-medium text-gray-900">
                                          {formatCurrency(insight.cpp)}
                                        </div>
                                      </div>
                                    )}
                                    {insight.conversions && (
                                      <div>
                                        <div className="text-gray-500">Conversions</div>
                                        <div className="font-medium text-gray-900">
                                          {typeof insight.conversions === 'string'
                                            ? formatNumber(insight.conversions)
                                            : formatNumber(
                                                insight.conversions
                                                  .reduce(
                                                    (sum, action) =>
                                                      sum + parseFloat(action.value || '0'),
                                                    0
                                                  )
                                                  .toString()
                                              )}
                                        </div>
                                      </div>
                                    )}
                                    {insight.cost_per_conversion &&
                                      Array.isArray(insight.cost_per_conversion) &&
                                      insight.cost_per_conversion.find(
                                        (c) => c.action_type === 'omni_purchase'
                                      ) && (
                                        <div>
                                          <div className="text-gray-500">Cost/Conv</div>
                                          <div className="font-medium text-orange-600">
                                            {formatCurrency(
                                              insight.cost_per_conversion.find(
                                                (c) => c.action_type === 'omni_purchase'
                                              )?.value || '0'
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>

                                  {/* Conversions Breakdown */}
                                  {insight.conversions &&
                                    typeof insight.conversions !== 'string' &&
                                    insight.conversions.length > 0 && (
                                      <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-2">
                                        <div className="mb-1 text-xs font-medium text-green-800">
                                          Conversion Types
                                        </div>
                                        <div className="space-y-1 text-xs">
                                          {insight.conversions.map((action, actionIdx) => (
                                            <div
                                              key={actionIdx}
                                              className="flex items-center justify-between"
                                            >
                                              <span className="text-green-700">
                                                {action.action_type?.replace(/_/g, ' ')}
                                              </span>
                                              <span className="font-medium text-green-900">
                                                {action.value}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                  {/* Cost Per Conversion Breakdown */}
                                  {insight.cost_per_conversion &&
                                    typeof insight.cost_per_conversion !== 'string' &&
                                    insight.cost_per_conversion.length > 0 && (
                                      <div className="mt-2 rounded-lg border border-purple-200 bg-purple-50 p-2">
                                        <div className="mb-1 text-xs font-medium text-purple-800">
                                          Cost Per Conversion Types
                                        </div>
                                        <div className="space-y-1 text-xs">
                                          {insight.cost_per_conversion.map((action, actionIdx) => (
                                            <div
                                              key={actionIdx}
                                              className="flex items-center justify-between"
                                            >
                                              <span className="text-purple-700">
                                                {action.action_type?.replace(/_/g, ' ')}
                                              </span>
                                              <span className="font-medium text-purple-900">
                                                {formatCurrency(action.value || '0')}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                  {/* Breakdown Demographics */}
                                  {(insight.age ||
                                    insight.gender ||
                                    insight.country ||
                                    insight.device_platform) && (
                                    <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-2">
                                      <div className="mb-1 text-xs font-medium text-blue-800">
                                        Demographics
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        {insight.age && (
                                          <div>
                                            <span className="text-blue-600">Age:</span>{' '}
                                            <span className="text-blue-900">{insight.age}</span>
                                          </div>
                                        )}
                                        {insight.gender && (
                                          <div>
                                            <span className="text-blue-600">Gender:</span>{' '}
                                            <span className="text-blue-900">{insight.gender}</span>
                                          </div>
                                        )}
                                        {insight.country && (
                                          <div>
                                            <span className="text-blue-600">Country:</span>{' '}
                                            <span className="text-blue-900">{insight.country}</span>
                                          </div>
                                        )}
                                        {insight.region && (
                                          <div>
                                            <span className="text-blue-600">Region:</span>{' '}
                                            <span className="text-blue-900">{insight.region}</span>
                                          </div>
                                        )}
                                        {insight.device_platform && (
                                          <div>
                                            <span className="text-blue-600">Device:</span>{' '}
                                            <span className="text-blue-900">
                                              {insight.device_platform}
                                            </span>
                                          </div>
                                        )}
                                        {insight.publisher_platform && (
                                          <div>
                                            <span className="text-blue-600">Platform:</span>{' '}
                                            <span className="text-blue-900">
                                              {insight.publisher_platform}
                                            </span>
                                          </div>
                                        )}
                                        {insight.platform_position && (
                                          <div>
                                            <span className="text-blue-600">Position:</span>{' '}
                                            <span className="text-blue-900">
                                              {insight.platform_position}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function downloadFacebookAdsGetAdDetails(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsGetAdDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsGetAdDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const results: FacebookAdsGetAdDetailsResult[] = Array.isArray(parseResult.data)
    ? parseResult.data
    : [parseResult.data]

  const result = results[0]
  const { ads, summary } = result

  if (ads.length === 0) {
    return `## Facebook Ad Details

No ad data found.

---
`
  }

  const summarySection = `### Summary

- **Ads Returned:** ${summary.total_ads}
${summary.total_spend !== null && summary.total_spend !== undefined ? `- **Total Spend:** $${summary.total_spend.toFixed(2)}` : ''}
${summary.total_impressions !== null && summary.total_impressions !== undefined ? `- **Total Impressions:** ${summary.total_impressions.toLocaleString()}` : ''}
${summary.total_clicks !== null && summary.total_clicks !== undefined ? `- **Total Clicks:** ${summary.total_clicks.toLocaleString()}` : ''}
${summary.total_revenue !== null && summary.total_revenue !== undefined ? `- **Total Revenue:** $${summary.total_revenue.toFixed(2)}` : ''}
${summary.total_conversions !== null && summary.total_conversions !== undefined ? `- **Total Conversions:** ${summary.total_conversions.toLocaleString()}` : ''}
${summary.roas !== null && summary.roas !== undefined && summary.roas > 0 ? `- **ROAS (Calculated):** ${summary.roas.toFixed(2)}x` : ''}
${summary.meta_roas !== null && summary.meta_roas !== undefined && summary.meta_roas > 0 ? `- **ROAS (Meta):** ${summary.meta_roas.toFixed(2)}x` : ''}
${summary.average_ctr !== null && summary.average_ctr !== undefined ? `- **Average CTR:** ${summary.average_ctr.toFixed(2)}%` : ''}
${summary.average_cpc !== null && summary.average_cpc !== undefined ? `- **Average CPC:** $${summary.average_cpc.toFixed(2)}` : ''}
${summary.average_cpm !== null && summary.average_cpm !== undefined ? `- **Average CPM:** $${summary.average_cpm.toFixed(2)}` : ''}
`

  const adsMarkdown = ads
    .map((ad) => {
      const creativeText = ad.creative
        ? `
**Creative:**
${ad.creative.id ? `- ID: ${ad.creative.id}` : ''}
${ad.creative.name ? `- Name: ${ad.creative.name}` : ''}
${ad.creative.title ? `- Title: ${ad.creative.title}` : ''}
${ad.creative.body ? `- Body: ${ad.creative.body}` : ''}
${ad.creative.image_hash ? `- Image Hash: ${ad.creative.image_hash}` : ''}
${ad.creative.video_id ? `- Video ID: ${ad.creative.video_id}` : ''}
${ad.creative.call_to_action_type ? `- Call to Action: ${ad.creative.call_to_action_type}` : ''}
${ad.creative.link_url ? `- Link URL: ${ad.creative.link_url}` : ''}
`
        : ''

      const insightsText =
        ad.insights && ad.insights.length > 0
          ? ad.insights
              .map(
                (insight) => `
**Insights:**
${insight.impressions ? `- Impressions: ${formatNumber(insight.impressions)}` : ''}
${insight.clicks ? `- Clicks: ${formatNumber(insight.clicks)}` : ''}
${insight.spend ? `- Spend: ${formatCurrency(insight.spend)}` : ''}
${insight.reach ? `- Reach: ${formatNumber(insight.reach)}` : ''}
${insight.frequency ? `- Frequency: ${parseFloat(insight.frequency).toFixed(2)}` : ''}
${insight.ctr ? `- CTR: ${formatPercent(insight.ctr)}` : ''}
${insight.cpc ? `- CPC: ${formatCurrency(insight.cpc)}` : ''}
${insight.cpm ? `- CPM: ${formatCurrency(insight.cpm)}` : ''}
${insight.cpp ? `- CPP: ${formatCurrency(insight.cpp)}` : ''}
${insight.conversions ? `- Conversions: ${typeof insight.conversions === 'string' ? formatNumber(insight.conversions) : formatNumber(insight.conversions.reduce((sum, action) => sum + parseFloat(action.value || '0'), 0).toString())}` : ''}
${insight.cost_per_conversion && Array.isArray(insight.cost_per_conversion) && insight.cost_per_conversion.find((c) => c.action_type === 'omni_purchase') ? `- Cost/Conversion: ${formatCurrency(insight.cost_per_conversion.find((c) => c.action_type === 'omni_purchase')?.value || '0')}` : ''}

${
  insight.age || insight.gender || insight.country || insight.device_platform
    ? `**Demographics:**
${insight.age ? `- Age: ${insight.age}` : ''}
${insight.gender ? `- Gender: ${insight.gender}` : ''}
${insight.country ? `- Country: ${insight.country}` : ''}
${insight.region ? `- Region: ${insight.region}` : ''}
${insight.device_platform ? `- Device: ${insight.device_platform}` : ''}
${insight.publisher_platform ? `- Platform: ${insight.publisher_platform}` : ''}
${insight.platform_position ? `- Position: ${insight.platform_position}` : ''}
`
    : ''
}
`
              )
              .join('\n')
          : ''

      return `### ${ad.ad_name}

**ID:** ${ad.ad_id}
${ad.status ? `**Status:** ${ad.status}` : ''}

${creativeText}
${insightsText}
`
    })
    .join('\n---\n\n')

  return `## Facebook Ad Details

${summarySection}

---

${adsMarkdown}

---
`
}

export const FacebookAdsGetAdDetailsDisplay = createWorkflowToolComponent(
  FacebookAdsGetAdDetailsDisplayComponent,
  downloadFacebookAdsGetAdDetails,
  facebookAdsGetAdDetailsToMarkdown
)
